import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import {
  getAllGiftCardsUsage,
  getAllGiftsCards,
  getGiftsCardList,
} from "store/actions/giftCardAction"

import formatDateTime from "components/Shared/formatDateTime"

const GiftCardUsage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Eidiyah Usage | Namq")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const flatpickrRef = useRef(null)
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [dateRange, setDateRange] = useState({})
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    giftCardsUsage,
    giftCardList,
  } = useSelector(state => state.GiftCard)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      38,
      "gift_card_usage_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      fetchAllRecords()
      fetchGiftCardList()
    }
  }, [userRole])

  const fetchGiftCardList = () => {
    dispatch(getGiftsCardList())
  }

  const sortFilter = {
    name: "sortOrder",
    type: "select",
    options: [
      { label: t("Ascending"), value: 1 },
      { label: t("Descending"), value: 2 },
    ],
    placeholder: t("Descending"),
  }

  const giftCardFilter = {
    name: "giftCardId",
    type: "singleSelect",
    options: [
      ...giftCardList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
    ],
    placeholder: t("Select Eidiyah"),
  }

  // Redeemed filter (dynamic)
  const redeemedFilter = {
    name: "redeemed",
    type: "singleSelect",
    options: [
      { label: t("All"), value: null },
      { label: t("Redeemed"), value: true },
      { label: t("Not Redeemed"), value: false },
    ],
    placeholder: t("Select Redeemed Status"),
  }

  const updatedFilters = [sortFilter, giftCardFilter, redeemedFilter]

  const [searchTerm, setSearchTerm] = useState("")
  const [searchTerm2, setSearchTerm2] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: 2,
    giftCardId: "",
    receiverNumber: "",
    redeemed: null, // default to null, meaning no filter (All)
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const exportToCSV = async () => {
    const { giftCardId, redeemed } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""

    const payload = {
      type: "GIFT-CARD-USAGE",
      exportType: "csv",
      giftCardId: updatedGiftCardId,
      redeemed: redeemed?.value || null, // Send redeemed as null, true, or false
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Eidiyah Reports"))
    }
  }

  const handleExcelDownload = async () => {
    const { giftCardId, redeemed } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""

    const payload = {
      type: "GIFT-CARD-USAGE",
      exportType: "xlsx",
      giftCardId: updatedGiftCardId,
      redeemed: redeemed?.value || null, // Send redeemed as null, true, or false
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Eidiyah Reports"))
    }
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue, // selectedValue will be true or false, not the entire object
    }))
  }

  const fetchAllRecords = () => {
    const { redeemed, giftCardId } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""
    // console.log(redeemed,'redeemed');

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: updatedGiftCardId,
      receiverNumber: "",
      userId: "",
      redeemed: redeemed?.value || null, // redeemed will be true or false, not an object
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllGiftCardsUsage(payload))
  }

  const getApiCall = (page = 1) => {
    const { sortOrder, giftCardId, redeemed } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      giftCardSearch: searchTerm2 || "",
      giftCardId: updatedGiftCardId,
      receiverNumber: "",
      userId: "",
      redeemed: redeemed?.value,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    return dispatch(getAllGiftCardsUsage(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/eidiyah-reports?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall()
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    setSearchTerm("")
    setSearchTerm2("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: "",
      receiverNumber: "",
      userId: "",
      redeemed: null,
      dateRange: {},
    }
    dispatch(getAllGiftCardsUsage(payload))
    navigate(`/eidiyah-reports?page=${1}`)
    setSelectedFilters({
      sortOrder: 2,
      giftCardId: "",
      receiverNumber: "",
      redeemed: null, // Reset redeemed filter to null (All)
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />

        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Eidiyah Reports")}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  filters={updatedFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Sender Name, Phone")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Eidiyah Name")}
                  colSize={4}
                  dateRange={dateRange}
                  isRangeDate={true}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : giftCardsUsage.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ maxWidth: 50 }}>{t("S. No.")}</th>
                          <th>{t("Eidiyah No")}</th>
                          <th>{t("Eidiyah Name")}</th>
                          <th>{t("Redeemed")}</th>
                          <th>{t("Sender Phone Number")}</th>
                          <th>{t("Receiver Phone Number")}</th>
                          <th>{t("Amount")}</th>
                          {/* <th>{t("Message")}</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {giftCardsUsage.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(
                                `/eidiyah-reports/details?id=${item._id}`
                              )
                            }}
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>{item?.giftCardNo ?? "-"}</td>
                            <td>
                              {language === "sa"
                                ? item.giftCardId?.nameAr
                                : item.giftCardId?.name || "-"}
                            </td>
                            <td>
                              {item.redeemed ? (
                                <Badge className="bg-success">
                                  {t("Redeemed")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Not Redeemed")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <p className="ltr w_fit">
                                {item?.senderId?.phone || "-"}
                              </p>
                            </td>
                            <td>
                              <p className="ltr w_fit">
                                {item?.receiverNumber || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.amount ?? "-"} {item?.currency}
                            </td>
                            {/* <td style={{ maxWidth: 230 }}>
                              <p className="in_2_line">{item?.message}</p>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && giftCardsUsage.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={giftCardsUsage}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default GiftCardUsage
